/**
 * ProspectDetail — full prospect detail page.
 *
 * Sections:
 *   1. Header with name + status + action bar (Move Stage, Convert, Archive,
 *      Resend Magic Link)
 *   2. Overview — applicant, representative, business, integration IDs (editable)
 *   3. Pipeline — current stage + status + history strip (stage_events)
 *   4. Tasks — per-task status grid grouped by status, with status dropdown +
 *      note field
 *   5. Documents — uploaded SS4 + others, with upload + download + replace
 *   6. Consent Audit — captured values + IP + UA + disclosure version IDs
 *   7. Affiliate — link card if attributed
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  CheckCircle2,
  Edit3,
  FileText,
  Loader2,
  Mail,
  RefreshCw,
  Save,
  Trash2,
  UploadCloud,
  XCircle,
} from 'lucide-react';
import {
  archiveProspect,
  convertProspect,
  getProspectDetail,
  getProspectDocumentDownloadUrl,
  getProspectDocumentViewUrl,
  markProspectTask,
  resendMagicLink,
  transitionProspectStage,
  updateProspect,
  uploadProspectDocument,
} from './api';
import type {
  ProspectDetailResponse,
  ProspectFull,
  StageStatus,
  TaskStatus,
  Stage,
  Task,
} from './types';

interface Props {
  prospectId: string;
  onBack: () => void;
}

// =============================================================================

export default function ProspectDetail({ prospectId, onBack }: Props) {
  const [data, setData] = useState<ProspectDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  // Modal state
  const [moveDialog, setMoveDialog] = useState(false);
  const [convertDialog, setConvertDialog] = useState(false);
  const [archiveDialog, setArchiveDialog] = useState(false);
  const [forceConvertText, setForceConvertText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await getProspectDetail(prospectId);
    if (!res.success || !res.data) {
      setError(res.error || 'Failed to load prospect.');
      setData(null);
    } else {
      setData(res.data);
    }
    setLoading(false);
  }, [prospectId]);

  useEffect(() => {
    load();
  }, [load]);

  const showToast = (kind: 'success' | 'error', text: string) => {
    setToast({ kind, text });
    window.setTimeout(() => setToast(null), 4000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
        <span className="text-gray-600">Loading prospect…</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div>
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error || 'Prospect not found.'}
        </div>
      </div>
    );
  }

  const { prospect, stage_events, task_statuses, documents, consents, stages, tasks, affiliate } =
    data;
  const currentStage = stages.find((s) => s.id === prospect.current_stage_id);
  const isReadOnly = !!prospect.converted_at;
  const isArchived = !!prospect.archived_at;

  // Convert preconditions
  const canConvertNormally =
    !isReadOnly &&
    !isArchived &&
    !!prospect.ghl_location_id &&
    !!prospect.stripe_customer_id &&
    !!prospect.a2p_fee_paid_at &&
    !!currentStage &&
    currentStage.position >= 60; // campaign_approved (position 60) or later

  // Whether to allow forced convert (preconditions met EXCEPT stage)
  const canForceConvert =
    !isReadOnly &&
    !isArchived &&
    !!prospect.ghl_location_id &&
    !!prospect.stripe_customer_id &&
    !!prospect.a2p_fee_paid_at &&
    !canConvertNormally;

  return (
    <div className="space-y-4 max-w-6xl">
      {/* Header */}
      <div>
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-3"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {prospect.applicant_first_name} {prospect.applicant_last_name}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {prospect.applicant_email}
              {prospect.applicant_phone ? ` · ${prospect.applicant_phone}` : ''}
            </p>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {currentStage && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                  {currentStage.display_name}
                </span>
              )}
              <StatusPill status={prospect.current_stage_status} />
              {isReadOnly && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">
                  <CheckCircle2 className="w-3 h-3" />
                  Converted
                </span>
              )}
              {isArchived && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-medium">
                  Archived
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {!isReadOnly && !isArchived && (
              <>
                <button
                  onClick={() => setMoveDialog(true)}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Move stage
                </button>
                <button
                  onClick={() => setConvertDialog(true)}
                  disabled={!canConvertNormally && !canForceConvert}
                  title={
                    !canConvertNormally
                      ? canForceConvert
                        ? 'Force convert available — type the prospect name to confirm.'
                        : 'Conversion preconditions not met: needs location ID, Stripe customer, and paid fee.'
                      : ''
                  }
                  className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Convert to Active Client
                </button>
                <button
                  onClick={async () => {
                    const r = await resendMagicLink(prospect.id);
                    if (!r.success) {
                      showToast('error', r.error || 'Failed to resend magic link');
                    } else if ((r.data as any)?.email_sent) {
                      showToast('success', 'Magic link emailed.');
                    } else {
                      const url = (r.data as any)?.magic_link_url;
                      showToast(
                        'success',
                        url
                          ? `Link created (email not sent — copy: ${url})`
                          : 'Link created. Email may not have sent.'
                      );
                    }
                  }}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1.5"
                >
                  <Mail className="w-4 h-4" />
                  Resend link
                </button>
                <button
                  onClick={() => setArchiveDialog(true)}
                  className="px-3 py-1.5 text-sm text-red-700 border border-red-200 rounded-md hover:bg-red-50 flex items-center gap-1.5"
                >
                  <Trash2 className="w-4 h-4" />
                  Archive
                </button>
              </>
            )}
            <button
              onClick={load}
              className="p-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-md shadow-lg text-sm ${
            toast.kind === 'success'
              ? 'bg-green-600 text-white'
              : 'bg-red-600 text-white'
          }`}
        >
          {toast.text}
        </div>
      )}

      {/* Section 1: Overview (editable) */}
      <OverviewSection
        prospect={prospect}
        isReadOnly={isReadOnly}
        onUpdated={load}
        onToast={showToast}
      />

      {/* Section 2: Pipeline + Event History */}
      <PipelineSection
        prospect={prospect}
        stages={stages}
        events={stage_events}
        currentStage={currentStage}
      />

      {/* Section 3: Tasks */}
      <TasksSection
        prospect={prospect}
        tasks={tasks}
        taskStatuses={task_statuses}
        isReadOnly={isReadOnly}
        onChanged={load}
        onToast={showToast}
      />

      {/* Section 4: Documents */}
      <DocumentsSection
        prospect={prospect}
        documents={documents}
        isReadOnly={isReadOnly}
        onChanged={load}
        onToast={showToast}
      />

      {/* Section 5: Consent Audit */}
      <ConsentAuditSection consents={consents} />

      {/* Section 6: Affiliate Attribution */}
      <AffiliateSection prospect={prospect} affiliate={affiliate} />

      {/* Modals */}
      {moveDialog && (
        <MoveStageDialog
          stages={stages}
          currentStageId={prospect.current_stage_id}
          onCancel={() => setMoveDialog(false)}
          onConfirm={async (toStageId, toStatus, note) => {
            const r = await transitionProspectStage(prospect.id, toStageId, toStatus, note);
            if (!r.success) {
              showToast('error', r.error || 'Move failed');
              return;
            }
            setMoveDialog(false);
            showToast('success', 'Stage updated.');
            await load();
          }}
        />
      )}

      {convertDialog && (
        <ConvertDialog
          prospect={prospect}
          canConvertNormally={canConvertNormally}
          canForceConvert={canForceConvert}
          forceConvertText={forceConvertText}
          onForceTextChange={setForceConvertText}
          onCancel={() => {
            setConvertDialog(false);
            setForceConvertText('');
          }}
          onConfirm={async (note) => {
            const r = await convertProspect(prospect.id, note);
            if (!r.success) {
              showToast('error', r.error || 'Conversion failed');
              return;
            }
            setConvertDialog(false);
            setForceConvertText('');
            const result = r.data as any;
            if (result?.warnings && result.warnings.length > 0) {
              showToast(
                'success',
                `Converted with ${result.warnings.length} warning(s) — see Issues log.`
              );
            } else {
              showToast('success', 'Conversion complete. Account is now active.');
            }
            await load();
          }}
        />
      )}

      {archiveDialog && (
        <ArchiveDialog
          onCancel={() => setArchiveDialog(false)}
          onConfirm={async (reason) => {
            const r = await archiveProspect(prospect.id, reason);
            if (!r.success) {
              showToast('error', r.error || 'Archive failed');
              return;
            }
            setArchiveDialog(false);
            showToast('success', 'Prospect archived.');
            await load();
          }}
        />
      )}
    </div>
  );
}

// =============================================================================
// Section: Overview (editable)
// =============================================================================

const EDITABLE_GROUPS: Array<{
  title: string;
  fields: Array<{ key: keyof ProspectFull; label: string; type?: 'text' | 'date' }>;
}> = [
  {
    title: 'Applicant',
    fields: [
      { key: 'applicant_first_name', label: 'First name' },
      { key: 'applicant_last_name', label: 'Last name' },
      { key: 'applicant_phone', label: 'Phone' },
      { key: 'applicant_street', label: 'Street' },
      { key: 'applicant_city', label: 'City' },
      { key: 'applicant_state', label: 'State' },
      { key: 'applicant_zip', label: 'ZIP' },
    ],
  },
  {
    title: 'Representative (only if partnered)',
    fields: [
      { key: 'representative_first_name', label: 'Rep first name' },
      { key: 'representative_last_name', label: 'Rep last name' },
      { key: 'representative_phone', label: 'Rep phone' },
    ],
  },
  {
    title: 'Business',
    fields: [
      { key: 'legal_business_name', label: 'Legal business name' },
      { key: 'ein', label: 'EIN' },
      { key: 'ein_issued_date', label: 'EIN issued date', type: 'date' },
      { key: 'business_street', label: 'Street' },
      { key: 'business_city', label: 'City' },
      { key: 'business_state', label: 'State' },
      { key: 'business_zip', label: 'ZIP' },
    ],
  },
  {
    title: 'Integration IDs',
    fields: [
      { key: 'ghl_location_id', label: 'CRM location ID' },
      { key: 'ghl_contact_id', label: 'CRM contact ID (Oasis)' },
    ],
  },
];

function OverviewSection({
  prospect,
  isReadOnly,
  onUpdated,
  onToast,
}: {
  prospect: ProspectFull;
  isReadOnly: boolean;
  onUpdated: () => void;
  onToast: (kind: 'success' | 'error', text: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Partial<ProspectFull>>({});
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    const next: Partial<ProspectFull> = {};
    for (const group of EDITABLE_GROUPS) {
      for (const f of group.fields) {
        (next as any)[f.key] = (prospect as any)[f.key] ?? '';
      }
    }
    setForm(next);
    setEditing(true);
  };

  const cancelEdit = () => {
    setForm({});
    setEditing(false);
  };

  const save = async () => {
    setSaving(true);
    // Send empty strings as nulls
    const payload: Record<string, any> = {};
    for (const [k, v] of Object.entries(form)) {
      payload[k] = v === '' ? null : v;
    }
    const r = await updateProspect(prospect.id, payload);
    setSaving(false);
    if (!r.success) {
      onToast('error', r.error || 'Save failed');
      return;
    }
    setEditing(false);
    onToast('success', 'Overview saved.');
    onUpdated();
  };

  return (
    <Card title="Overview" right={
      !isReadOnly && (
        editing ? (
          <div className="flex items-center gap-2">
            <button
              onClick={cancelEdit}
              disabled={saving}
              className="px-2.5 py-1 text-xs border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-1 px-2.5 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-3 h-3" />
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        ) : (
          <button
            onClick={startEdit}
            className="flex items-center gap-1 px-2.5 py-1 text-xs border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Edit3 className="w-3 h-3" />
            Edit
          </button>
        )
      )
    }>
      {EDITABLE_GROUPS.map((group) => (
        <div key={group.title} className="mb-4 last:mb-0">
          <h4 className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-2">
            {group.title}
          </h4>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {group.fields.map((f) => {
              const val = editing
                ? ((form[f.key] as any) ?? '')
                : ((prospect[f.key] as any) ?? '');
              return (
                <div key={String(f.key)} className="flex flex-col">
                  <dt className="text-xs text-gray-500">{f.label}</dt>
                  <dd>
                    {editing ? (
                      <input
                        type={f.type === 'date' ? 'date' : 'text'}
                        value={val || ''}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, [f.key]: e.target.value }))
                        }
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    ) : (
                      <span className="text-gray-900">
                        {val ? (
                          String(val)
                        ) : (
                          <span className="text-gray-400 italic">—</span>
                        )}
                      </span>
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
      ))}
      <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
        Source: <span className="font-mono">{prospect.source}</span>
        {prospect.referral_code_used ? ` · code ${prospect.referral_code_used}` : ''}
        {' · created '}
        {new Date(prospect.created_at).toLocaleString()}
      </div>
    </Card>
  );
}

// =============================================================================
// Section: Pipeline + Event History
// =============================================================================

function PipelineSection({
  prospect,
  stages,
  events,
  currentStage,
}: {
  prospect: ProspectFull;
  stages: Stage[];
  events: ProspectDetailResponse['stage_events'];
  currentStage?: Stage;
}) {
  const stageById = useMemo(() => {
    const m = new Map<string, Stage>();
    for (const s of stages) m.set(s.id, s);
    return m;
  }, [stages]);

  return (
    <Card title="Pipeline">
      <div className="mb-3 text-sm">
        <div>
          Currently in{' '}
          <span className="font-medium text-gray-900">
            {currentStage?.display_name || 'unknown stage'}
          </span>{' '}
          for {formatTimeInStage(prospect.entered_current_stage_at)}.
        </div>
      </div>

      <h4 className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-2">
        History
      </h4>
      {events.length === 0 ? (
        <p className="text-sm text-gray-500 italic">No stage events yet.</p>
      ) : (
        <ul className="space-y-2 text-sm">
          {events.map((e) => {
            const fromStage = e.from_stage_id ? stageById.get(e.from_stage_id) : null;
            const toStage = stageById.get(e.to_stage_id);
            return (
              <li
                key={e.id}
                className="flex items-start gap-3 border-l-2 border-gray-200 pl-3 py-1"
              >
                <div className="flex-1">
                  <div className="text-gray-900">
                    {fromStage ? (
                      <>
                        {fromStage.display_name}{' '}
                        <span className="text-gray-400">→</span>{' '}
                      </>
                    ) : (
                      <span className="text-gray-500 italic">created · </span>
                    )}
                    <span className="font-medium">
                      {toStage?.display_name || 'unknown'}
                    </span>
                    <span className="ml-1 text-gray-500">({e.to_stage_status})</span>
                  </div>
                  {e.note && (
                    <p className="mt-0.5 text-xs text-gray-600">{e.note}</p>
                  )}
                </div>
                <div className="text-xs text-gray-500 whitespace-nowrap">
                  {e.actor_type}
                  {' · '}
                  {new Date(e.created_at).toLocaleString()}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

// =============================================================================
// Section: Tasks
// =============================================================================

const TASK_STATUS_ORDER: TaskStatus[] = [
  'ready',
  'in_progress',
  'blocked',
  'complete',
  'not_ready',
  'skipped',
];

const TASK_STATUS_LABEL: Record<TaskStatus, string> = {
  not_ready: 'Not ready',
  ready: 'Ready',
  in_progress: 'In progress',
  blocked: 'Blocked',
  complete: 'Complete',
  skipped: 'Skipped',
};

function TasksSection({
  prospect,
  tasks,
  taskStatuses,
  isReadOnly,
  onChanged,
  onToast,
}: {
  prospect: ProspectFull;
  tasks: Task[];
  taskStatuses: ProspectDetailResponse['task_statuses'];
  isReadOnly: boolean;
  onChanged: () => void;
  onToast: (kind: 'success' | 'error', text: string) => void;
}) {
  const taskById = useMemo(() => {
    const m = new Map<string, Task>();
    for (const t of tasks) m.set(t.id, t);
    return m;
  }, [tasks]);

  const grouped = useMemo(() => {
    const groups = new Map<TaskStatus, ProspectDetailResponse['task_statuses']>();
    for (const status of TASK_STATUS_ORDER) groups.set(status, []);
    for (const ts of taskStatuses) {
      if (!groups.has(ts.status)) groups.set(ts.status, []);
      groups.get(ts.status)!.push(ts);
    }
    return groups;
  }, [taskStatuses]);

  const handleStatusChange = async (taskId: string, to: TaskStatus) => {
    const r = await markProspectTask(prospect.id, taskId, to);
    if (!r.success) {
      onToast('error', r.error || 'Failed to update task');
      return;
    }
    onChanged();
  };

  return (
    <Card title="Tasks">
      {taskStatuses.length === 0 ? (
        <p className="text-sm text-gray-500 italic">No tasks for this prospect.</p>
      ) : (
        <div className="space-y-3">
          {TASK_STATUS_ORDER.map((status) => {
            const rows = grouped.get(status) || [];
            if (rows.length === 0) return null;
            return (
              <div key={status}>
                <h4 className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1.5">
                  {TASK_STATUS_LABEL[status]} ({rows.length})
                </h4>
                <ul className="space-y-1.5">
                  {rows.map((ts) => {
                    const task = taskById.get(ts.task_id);
                    if (!task) return null;
                    return (
                      <li
                        key={ts.id}
                        className="flex items-start justify-between gap-3 bg-gray-50 rounded px-3 py-2 text-sm"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 truncate">
                              {task.display_name}
                            </span>
                            {task.automation_enabled && (
                              <span className="text-[10px] uppercase tracking-wide px-1 py-0.5 bg-purple-100 text-purple-800 rounded">
                                Automated
                              </span>
                            )}
                          </div>
                          {task.description && (
                            <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">
                              {task.description}
                            </p>
                          )}
                          {ts.note && (
                            <p className="text-xs text-gray-700 mt-0.5 italic">
                              Note: {ts.note}
                            </p>
                          )}
                        </div>
                        <select
                          value={ts.status}
                          onChange={(e) =>
                            handleStatusChange(task.id, e.target.value as TaskStatus)
                          }
                          disabled={isReadOnly}
                          className="text-xs border border-gray-300 rounded px-1.5 py-1 disabled:opacity-50"
                        >
                          {TASK_STATUS_ORDER.map((s) => (
                            <option key={s} value={s}>
                              {TASK_STATUS_LABEL[s]}
                            </option>
                          ))}
                        </select>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// =============================================================================
// Section: Documents
// =============================================================================

function DocumentsSection({
  prospect,
  documents,
  isReadOnly,
  onChanged,
  onToast,
}: {
  prospect: ProspectFull;
  documents: ProspectDetailResponse['documents'];
  isReadOnly: boolean;
  onChanged: () => void;
  onToast: (kind: 'success' | 'error', text: string) => void;
}) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file: File) => {
    setUploading(true);
    const r = await uploadProspectDocument(prospect.id, file, 'ss4');
    setUploading(false);
    if (!r.success) {
      onToast('error', r.error || 'Upload failed');
      return;
    }
    onToast('success', 'Document uploaded.');
    onChanged();
  };

  return (
    <Card
      title="Documents"
      right={
        !isReadOnly && (
          <label
            className={`flex items-center gap-1.5 px-2.5 py-1 text-xs border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 ${
              uploading ? 'opacity-50 pointer-events-none' : ''
            }`}
          >
            <UploadCloud className="w-3.5 h-3.5" />
            {uploading ? 'Uploading…' : 'Upload SS4'}
            <input
              type="file"
              accept="application/pdf,image/png,image/jpeg"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUpload(f);
                e.target.value = '';
              }}
              className="hidden"
            />
          </label>
        )
      }
    >
      {documents.length === 0 ? (
        <p className="text-sm text-gray-500 italic">No documents uploaded yet.</p>
      ) : (
        <ul className="space-y-2">
          {documents.map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between gap-3 bg-gray-50 rounded px-3 py-2 text-sm"
            >
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <FileText className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {d.original_filename || '(unnamed)'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {d.document_type.toUpperCase()} · {d.uploaded_by_actor_type} ·{' '}
                    {new Date(d.uploaded_at).toLocaleString()}
                    {d.size_bytes ? ` · ${(d.size_bytes / 1024).toFixed(1)} KB` : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <a
                  href={getProspectDocumentViewUrl(prospect.id, d.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                >
                  View
                </a>
                <a
                  href={getProspectDocumentDownloadUrl(prospect.id, d.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Download
                </a>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// =============================================================================
// Section: Consent Audit (read-only)
// =============================================================================

function ConsentAuditSection({
  consents,
}: {
  consents: ProspectDetailResponse['consents'];
}) {
  if (!consents) {
    return (
      <Card title="Consent Audit">
        <p className="text-sm text-gray-500 italic">No consent snapshot recorded.</p>
      </Card>
    );
  }
  return (
    <Card title="Consent Audit">
      <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
        <Consent label="SMS service" value={consents.sms_service_consent} />
        <Consent label="SMS marketing" value={consents.sms_marketing_consent} />
        <Consent label="Terms agreement" value={consents.tc_agreement} />
        <div>
          <dt className="text-xs text-gray-500">Captured at</dt>
          <dd className="text-gray-900">{new Date(consents.captured_at).toLocaleString()}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">IP address</dt>
          <dd className="text-gray-900 font-mono text-xs">{consents.captured_ip_address}</dd>
        </div>
        <div className="md:col-span-3">
          <dt className="text-xs text-gray-500">User agent</dt>
          <dd className="text-gray-900 text-xs">{consents.captured_user_agent}</dd>
        </div>
        <div className="md:col-span-3">
          <dt className="text-xs text-gray-500 mb-1">Disclosure versions seen</dt>
          <dd>
            <ul className="text-xs text-gray-700 space-y-0.5">
              {consents.disclosures_seen.map((d) => (
                <li key={d.disclosure_id} className="font-mono">
                  {d.policy_key} v{d.version}
                </li>
              ))}
            </ul>
          </dd>
        </div>
      </dl>
    </Card>
  );
}

function Consent({ label, value }: { label: string; value: boolean }) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="flex items-center gap-1 mt-0.5">
        {value ? (
          <>
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span className="text-gray-900">Yes</span>
          </>
        ) : (
          <>
            <XCircle className="w-4 h-4 text-gray-400" />
            <span className="text-gray-500">No</span>
          </>
        )}
      </dd>
    </div>
  );
}

// =============================================================================
// Section: Affiliate
// =============================================================================

function AffiliateSection({
  prospect,
  affiliate,
}: {
  prospect: ProspectFull;
  affiliate: ProspectDetailResponse['affiliate'];
}) {
  if (!affiliate) {
    return (
      <Card title="Affiliate attribution">
        <p className="text-sm text-gray-500 italic">
          Not attributed to an affiliate
          {prospect.source === 'signup' ? ' (general signup form)' : ''}.
        </p>
      </Card>
    );
  }
  return (
    <Card title="Affiliate attribution">
      <div className="text-sm">
        <p className="font-medium text-gray-900">{affiliate.display_name}</p>
        <p className="text-gray-600">{affiliate.email}</p>
        <p className="text-xs text-gray-500 mt-1">
          Code: <span className="font-mono">{affiliate.referral_code}</span> · Status:{' '}
          {affiliate.status}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Submitted with code:{' '}
          <span className="font-mono">{prospect.referral_code_used || '—'}</span>
        </p>
      </div>
    </Card>
  );
}

// =============================================================================
// Dialog: Move Stage
// =============================================================================

const STAGE_STATUS_OPTIONS: Array<{ value: StageStatus; label: string }> = [
  { value: 'awaiting_admin', label: 'Awaiting us' },
  { value: 'awaiting_client', label: 'Awaiting client' },
  { value: 'awaiting_external', label: 'Awaiting review' },
  { value: 'rejected_needs_fix', label: 'Needs fix' },
  { value: 'complete', label: 'Complete' },
];

function MoveStageDialog({
  stages,
  currentStageId,
  onCancel,
  onConfirm,
}: {
  stages: Stage[];
  currentStageId: string;
  onCancel: () => void;
  onConfirm: (toStageId: string, toStatus: StageStatus, note?: string) => Promise<void>;
}) {
  const [toStageId, setToStageId] = useState(currentStageId);
  const [toStatus, setToStatus] = useState<StageStatus>('awaiting_admin');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  return (
    <Modal title="Move stage" onClose={onCancel}>
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Target stage</label>
          <select
            value={toStageId}
            onChange={(e) => setToStageId(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
          >
            {stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.display_name}
                {s.id === currentStageId ? ' (current)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Sub-status</label>
          <select
            value={toStatus}
            onChange={(e) => setToStatus(e.target.value as StageStatus)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
          >
            {STAGE_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">
            Note <span className="text-gray-400">(optional)</span>
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
          />
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button
          onClick={onCancel}
          disabled={submitting}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={async () => {
            setSubmitting(true);
            await onConfirm(toStageId, toStatus, note.trim() || undefined);
            setSubmitting(false);
          }}
          disabled={submitting}
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Moving…' : 'Move'}
        </button>
      </div>
    </Modal>
  );
}

// =============================================================================
// Dialog: Convert (with force-convert escape hatch)
// =============================================================================

function ConvertDialog({
  prospect,
  canConvertNormally,
  canForceConvert,
  forceConvertText,
  onForceTextChange,
  onCancel,
  onConfirm,
}: {
  prospect: ProspectFull;
  canConvertNormally: boolean;
  canForceConvert: boolean;
  forceConvertText: string;
  onForceTextChange: (v: string) => void;
  onCancel: () => void;
  onConfirm: (note?: string) => Promise<void>;
}) {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isForceMode = !canConvertNormally && canForceConvert;
  const expectedFullName = `${prospect.applicant_first_name} ${prospect.applicant_last_name}`;
  const forceConfirmed = forceConvertText.trim() === expectedFullName;
  const canSubmit = canConvertNormally || (isForceMode && forceConfirmed);

  return (
    <Modal title="Convert to Active Client" onClose={onCancel}>
      <div className="text-sm text-gray-700 space-y-3">
        <p>This will:</p>
        <ul className="list-disc ml-5 space-y-1 text-xs">
          <li>Create or update the location billing config</li>
          <li>Record the $75 charge in the wallet history</li>
          {prospect.affiliate_id && <li>Create the affiliate referral row</li>}
          <li>Move the prospect to Active Client</li>
        </ul>
        {isForceMode && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
            <strong>Force conversion:</strong> the prospect is not at Campaign Approved or
            later. To confirm, type the prospect's full name (
            <span className="font-mono">{expectedFullName}</span>) below.
            <input
              type="text"
              value={forceConvertText}
              onChange={(e) => onForceTextChange(e.target.value)}
              placeholder="Type full name to confirm"
              className="mt-2 w-full px-2 py-1.5 border border-amber-300 rounded-md text-sm"
            />
          </div>
        )}
        <div>
          <label className="block text-xs text-gray-500 mb-1">
            Note <span className="text-gray-400">(optional)</span>
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
          />
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button
          onClick={onCancel}
          disabled={submitting}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={async () => {
            setSubmitting(true);
            await onConfirm(note.trim() || undefined);
            setSubmitting(false);
          }}
          disabled={!canSubmit || submitting}
          className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Converting…' : 'Convert'}
        </button>
      </div>
    </Modal>
  );
}

// =============================================================================
// Dialog: Archive
// =============================================================================

function ArchiveDialog({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  return (
    <Modal title="Archive prospect" onClose={onCancel}>
      <div className="text-sm space-y-3">
        <p className="text-gray-700">
          Archived prospects are hidden from the kanban and list view by default. This is
          soft-delete only — the record stays in the database. Reversible.
        </p>
        <div>
          <label className="block text-xs text-gray-500 mb-1">
            Reason <span className="text-red-600">(required)</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Spam, duplicate, abandoned, etc."
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
          />
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button
          onClick={onCancel}
          disabled={submitting}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={async () => {
            setSubmitting(true);
            await onConfirm(reason.trim());
            setSubmitting(false);
          }}
          disabled={submitting || !reason.trim()}
          className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Archiving…' : 'Archive'}
        </button>
      </div>
    </Modal>
  );
}

// =============================================================================
// Shared UI primitives
// =============================================================================

function Card({
  title,
  right,
  children,
}: {
  title: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-gray-900 mb-3">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: StageStatus }) {
  const config: Record<StageStatus, { label: string; cls: string }> = {
    awaiting_client: { label: 'Awaiting client', cls: 'bg-amber-100 text-amber-800' },
    awaiting_admin: { label: 'Awaiting us', cls: 'bg-blue-100 text-blue-800' },
    awaiting_external: { label: 'Awaiting review', cls: 'bg-purple-100 text-purple-800' },
    rejected_needs_fix: { label: 'Needs fix', cls: 'bg-red-100 text-red-800' },
    complete: { label: 'Complete', cls: 'bg-green-100 text-green-800' },
  };
  const c = config[status] || config.awaiting_admin;
  return (
    <span
      className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${c.cls}`}
    >
      {c.label}
    </span>
  );
}

function formatTimeInStage(enteredAt: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(enteredAt).getTime()) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}
